/**
 * The visitor's stable identity, carried on every chat request as
 * `requestAttributes.userId`.
 *
 * A session is one conversation; a userId is the person across all of them.
 * The runtime keys persistent memory on this value, so it has to survive
 * "New chat", a reload, and a visit next week — which is why it lives in its
 * own long-lived cookie rather than riding along with the session cookie.
 *
 * Resolution is simply: read the cookie, and mint + persist one if it is
 * missing. There is no query-param override the way sessions have — a link
 * cannot hand you someone else's identity, because that would let one visitor
 * read another's memory.
 */

import { customAlphabet } from 'nanoid';
import { readCookie, writeCookie, deleteCookie } from './cookie-util';
import { USER_COOKIE_KEY, USER_COOKIE_MAX_AGE_DAYS } from '../../consts/common-consts';

// Same alphabet as session ids so the value stays URL- and log-safe. Longer,
// because these ids are far more numerous and much longer-lived than sessions,
// and a collision would silently merge two visitors' memory.
const nanoid = customAlphabet('1234567890abcdefghikkl', 24);

// Distinguishable at a glance in a trace or a memory partition key, where it
// otherwise reads as an anonymous blob.
const USER_ID_PREFIX = 'u_';

export function newUserId() {
  return `${USER_ID_PREFIX}${nanoid(20)}`;
}

/**
 * Memoized for the lifetime of the page load, for the same reason sessions
 * are: minting writes the cookie, so a second resolution — a StrictMode
 * remount, or another component asking — would read that cookie back and
 * report `created: false`. Nothing downstream depends on `created` today, but
 * the memo also keeps every caller on one id even if cookies are blocked.
 */
let resolvedForThisPageLoad = null;

/**
 * The visitor's id for this page load.
 *
 * @returns {{userId: string, created: boolean, source: 'cookie'|'new'}}
 */
export function resolveUserId() {
  if (resolvedForThisPageLoad) {
    return resolvedForThisPageLoad;
  }

  const cookieUserId = readCookie(USER_COOKIE_KEY);
  if (cookieUserId) {
    // Refresh the expiry so an active visitor is not aged out mid-relationship;
    // the cookie is a rolling year from the last visit, not from first contact.
    writeCookie(USER_COOKIE_KEY, cookieUserId, USER_COOKIE_MAX_AGE_DAYS);
    resolvedForThisPageLoad = { userId: cookieUserId, created: false, source: 'cookie' };
    return resolvedForThisPageLoad;
  }

  const userId = newUserId();
  writeCookie(USER_COOKIE_KEY, userId, USER_COOKIE_MAX_AGE_DAYS);
  resolvedForThisPageLoad = { userId, created: true, source: 'new' };
  return resolvedForThisPageLoad;
}

/** Just the id — the common case, where the caller does not care if it is new. */
export function getUserId() {
  return resolveUserId().userId;
}

/**
 * Drop the memoized resolution.
 * Exported so tests can simulate a fresh page load; in the app the memo lives
 * exactly as long as the document does.
 */
export function clearResolvedUser() {
  resolvedForThisPageLoad = null;
}

/**
 * Forget this visitor and become someone new.
 *
 * This is the "clear my data" action: the next request carries a different
 * userId, so the agent starts from no memory. It does not delete what is
 * already stored server-side — that is the console's job — it just stops
 * this browser from identifying as that person.
 */
export function resetUserId() {
  deleteCookie(USER_COOKIE_KEY);
  clearResolvedUser();
  return getUserId();
}

/**
 * Adopt a known identity, for hosts that have their own logged-in user.
 *
 * An embedding page that already knows who the visitor is should pass that id
 * through instead of letting the channel mint an anonymous one, so memory
 * follows the person across devices rather than across browser profiles.
 */
export function rememberUserId(userId) {
  const trimmed = (userId || '').trim();
  if (!trimmed) {
    return getUserId();
  }
  writeCookie(USER_COOKIE_KEY, trimmed, USER_COOKIE_MAX_AGE_DAYS);
  resolvedForThisPageLoad = { userId: trimmed, created: false, source: 'cookie' };
  return trimmed;
}
